import { nullableArray, nullableOptional } from '@shigo-ui/api-client';
import { z } from 'zod';

import {
  DashboardDatePreset,
  type DashboardDateRange,
  DashboardWidgetWidth,
} from '@/components/dashboard/model';

export enum DashboardDateScopeMode {
  Inherit = 'INHERIT',
  Preset = 'PRESET',
  Custom = 'CUSTOM',
}

export enum DashboardScope {
  Main = 'MAIN',
  SuperAdmin = 'SUPER_ADMIN',
}

export enum DashboardWidgetType {
  Metric = 'METRIC',
  PerformanceChart = 'PERFORMANCE_CHART',
  PublisherPerformance = 'PUBLISHER_PERFORMANCE',
  DemandIntegrations = 'DEMAND_INTEGRATIONS',
  DemandPartners = 'DEMAND_PARTNERS',
  SrpmRanking = 'SRPM_RANKING',
  NetworkPerformance = 'NETWORK_PERFORMANCE',
}

export enum DashboardWidgetAction {
  ViewInReporting = 'VIEW_IN_REPORTING',
  // Retained for legacy dashboard document parsing; this action is intentionally not rendered.
  PerformanceAnalyzer = 'PERFORMANCE_ANALYZER',
}

export enum DashboardMetric {
  AdRequests = 'AD_REQUESTS',
  AdClicks = 'AD_CLICKS',
  AdImpressions = 'AD_IMPRESSIONS',
  FillRate = 'FILL_RATE',
  Rpm = 'RPM',
  Srpm = 'SRPM',
  Revenue = 'REVENUE',
  PublisherRevenue = 'PUBLISHER_REVENUE',
  Profit = 'PROFIT',
  NetworkRevenue = 'NETWORK_REVENUE',
  SuperAdminProfit = 'SUPER_ADMIN_PROFIT',
}

export enum DashboardNetworkColumn {
  Network = 'NETWORK',
  AdRequests = 'AD_REQUESTS',
  AdImpressions = 'AD_IMPRESSIONS',
  Revenue = 'REVENUE',
  NetworkProfit = 'NETWORK_PROFIT',
}

export enum DashboardPublisherColumn {
  Publisher = 'PUBLISHER',
  AdRequests = 'AD_REQUESTS',
  AdImpressions = 'AD_IMPRESSIONS',
  FillRate = 'FILL_RATE',
  Rpm = 'RPM',
  Srpm = 'SRPM',
  RevenuePercentage = 'REVENUE_PERCENTAGE',
  MarginPercentage = 'MARGIN_PERCENTAGE',
  Revenue = 'REVENUE',
  PublisherRevenue = 'PUBLISHER_REVENUE',
  Profit = 'PROFIT',
}

export type DashboardOverviewFilter = DashboardDateRange;

export {
  DashboardDatePreset,
  type DashboardDateRange,
  DashboardWidgetWidth,
} from '@/components/dashboard/model';

const optionalNumber = nullableOptional(z.number());
const entityIdSchema = z.union([z.string(), z.number()]).transform(String);

export const dashboardMetricValuesSchema = z.looseObject({
  adClicks: optionalNumber,
  adRequests: optionalNumber,
  adImpressions: optionalNumber,
  adRequestsFilled: optionalNumber,
  rpm: optionalNumber,
  srpm: optionalNumber,
  revenue: optionalNumber,
  pubRevenue: optionalNumber,
  networkRevenue: optionalNumber,
  networkProfit: optionalNumber,
  profit: optionalNumber,
  marginPct: optionalNumber,
  networkMarginPct: optionalNumber,
});

export const dashboardTimelinePointSchema = dashboardMetricValuesSchema.extend({
  day: z.string(),
  hour: z.union([z.string(), z.number()]).nullish(),
});

export const dashboardPublisherRowSchema = dashboardMetricValuesSchema.extend({
  publisherId: entityIdSchema,
  publisherName: z.string(),
  adRequestsChange: optionalNumber,
  adImpressionsChange: optionalNumber,
  adRequestsFilledChange: optionalNumber,
  rpmChange: optionalNumber,
  srpmChange: optionalNumber,
  revenuePctChange: optionalNumber,
  networkMarginPctChange: optionalNumber,
  revenueChange: optionalNumber,
  pubRevenueChange: optionalNumber,
  networkProfitChange: optionalNumber,
  revenuePct: optionalNumber,
  global7daysRevenuePct: optionalNumber,
});

export const dashboardNetworkRowSchema = dashboardMetricValuesSchema.extend({
  networkId: nullableOptional(entityIdSchema),
  networkName: z.string(),
  adRequestsChange: optionalNumber,
  adImpressionsChange: optionalNumber,
  revenueChange: optionalNumber,
  networkProfitChange: optionalNumber,
});

export const dashboardSrpmTrendPointSchema = z.object({
  date: z.string(),
  srpm: z.number().nullable(),
});

export const dashboardSrpmRankingItemSchema = z.object({
  demandIntegrationId: z.string(),
  demandPartnerId: z.string(),
  demandIntegrationName: z.string(),
  revenue: z.number(),
  revenueChange: optionalNumber,
  bidRequests: z.number().int().nonnegative(),
  bidRequestsChange: optionalNumber,
  srpm: z.number(),
  previousSrpm: z.number().nullable(),
  srpmChange: z.number().nullable(),
  srpmTrend7d: z.array(z.object({ date: z.iso.date(), srpm: z.number().nullable() })).length(7),
});

export const dashboardDemandIntegrationRowSchema = dashboardMetricValuesSchema.extend({
  demandIntegrationId: nullableOptional(entityIdSchema),
  demandIntegrationName: z.string(),
  demandPartnerId: nullableOptional(entityIdSchema),
  bidRequests: optionalNumber,
  bidRequestsChange: optionalNumber,
  bidResponses: optionalNumber,
  bidResponsesChange: optionalNumber,
  bids: optionalNumber,
  bidRatio: optionalNumber,
  bidRatioChange: optionalNumber,
  winRatio: optionalNumber,
  winRatioChange: optionalNumber,
  adRequestsFilledChange: optionalNumber,
  previousSrpm: optionalNumber,
  srpmChange: optionalNumber,
  srpmTrend7d: nullableOptional(z.array(dashboardSrpmTrendPointSchema)),
  revenueChange: optionalNumber,
});

export const dashboardDemandPartnerRowSchema = dashboardMetricValuesSchema.extend({
  demandPartnerId: nullableOptional(entityIdSchema),
  demandPartnerName: z.string(),
  dpAdRequests: optionalNumber,
  dpAdRequestsChange: optionalNumber,
  bidFillRatio: optionalNumber,
  bidFillRatioChange: optionalNumber,
  adImpressionsChange: optionalNumber,
  bids: optionalNumber,
  bidsChange: optionalNumber,
  bidRatio: optionalNumber,
  bidRatioChange: optionalNumber,
  winRatio: optionalNumber,
  winRatioChange: optionalNumber,
  wonBids: optionalNumber,
  wonBidsChange: optionalNumber,
  revenueCapEnabled: z.boolean().nullish(),
  revenueCap: optionalNumber,
  adRequestsFilledChange: optionalNumber,
  marginPctChange: optionalNumber,
  srpmChange: optionalNumber,
  revenueChange: optionalNumber,
});

export const dashboardOverviewSchema = z.looseObject({
  previousPeriodEnd: z.iso.datetime({ offset: true }).nullish(),
  top10BySRPM: z.array(dashboardSrpmRankingItemSchema).max(10).optional(),
  bottom10BySRPM: z.array(dashboardSrpmRankingItemSchema).max(10).optional(),
  total: dashboardMetricValuesSchema.nullish().transform((value) => value ?? {}),
  totalPrevious: dashboardMetricValuesSchema.nullish().transform((value) => value ?? null),
  hours: nullableArray(dashboardTimelinePointSchema),
  totalsByDay: nullableArray(dashboardTimelinePointSchema),
  publishers: nullableArray(dashboardPublisherRowSchema),
  networks: nullableArray(dashboardNetworkRowSchema),
  demandIntegrations: nullableArray(dashboardDemandIntegrationRowSchema),
  demandPartnerAlerts: nullableArray(dashboardDemandPartnerRowSchema),
});

export const dashboardLastImportSchema = z.union([
  z.iso.datetime({ offset: true }),
  z.iso.datetime({ local: true }).transform((value) => `${value}Z`),
]);

export type DashboardMetricValues = z.infer<typeof dashboardMetricValuesSchema>;
export type DashboardTimelinePoint = z.infer<typeof dashboardTimelinePointSchema>;
export type DashboardPublisherRow = z.infer<typeof dashboardPublisherRowSchema>;
export type DashboardNetworkRow = z.infer<typeof dashboardNetworkRowSchema>;
export type DashboardSrpmTrendPoint = z.infer<typeof dashboardSrpmTrendPointSchema>;
export type DashboardSrpmRankingItem = z.infer<typeof dashboardSrpmRankingItemSchema>;
export type DashboardDemandIntegrationRow = z.infer<typeof dashboardDemandIntegrationRowSchema>;
export type DashboardDemandPartnerRow = z.infer<typeof dashboardDemandPartnerRowSchema>;
export type DashboardOverview = z.infer<typeof dashboardOverviewSchema>;

export const dashboardMetricValueField = {
  [DashboardMetric.AdClicks]: 'adClicks',
  [DashboardMetric.AdRequests]: 'adRequests',
  [DashboardMetric.AdImpressions]: 'adImpressions',
  [DashboardMetric.FillRate]: 'adRequestsFilled',
  [DashboardMetric.Rpm]: 'rpm',
  [DashboardMetric.Srpm]: 'srpm',
  [DashboardMetric.Revenue]: 'revenue',
  [DashboardMetric.PublisherRevenue]: 'pubRevenue',
  [DashboardMetric.Profit]: 'networkProfit',
  [DashboardMetric.NetworkRevenue]: 'networkRevenue',
  [DashboardMetric.SuperAdminProfit]: 'profit',
} as const satisfies Record<DashboardMetric, keyof DashboardMetricValues>;

const inheritDateScopeSchema = z.object({ mode: z.literal(DashboardDateScopeMode.Inherit) });
const presetDateScopeSchema = z.object({
  mode: z.literal(DashboardDateScopeMode.Preset),
  preset: z.enum(DashboardDatePreset),
});
const customDateScopeSchema = z.object({
  mode: z.literal(DashboardDateScopeMode.Custom),
  dateFrom: z.string(),
  dateTo: z.string(),
});

const dashboardWidgetActionsSchema = z
  .array(z.enum(DashboardWidgetAction))
  .superRefine((actions, context) => {
    if (new Set(actions).size !== actions.length) {
      context.addIssue({ code: 'custom', message: 'Dashboard widget actions must be unique.' });
    }
  })
  .default([]);

export const dashboardDateScopeSchema = z.discriminatedUnion('mode', [
  inheritDateScopeSchema,
  presetDateScopeSchema,
  customDateScopeSchema,
]);

const widgetBaseShape = {
  id: z.string().trim().min(1),
  width: z.enum(DashboardWidgetWidth),
  title: z.string().trim().min(1).optional(),
  dateScope: dashboardDateScopeSchema,
  actions: dashboardWidgetActionsSchema,
};

export const dashboardMetricWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.literal(DashboardWidgetType.Metric),
  metric: z.enum(DashboardMetric),
});

export const dashboardPerformanceChartWidgetSchema = z
  .object({
    ...widgetBaseShape,
    type: z.literal(DashboardWidgetType.PerformanceChart),
    metrics: z.array(z.enum(DashboardMetric)).min(1),
    defaultVisibleMetrics: z.array(z.enum(DashboardMetric)).optional(),
  })
  .refine(
    ({ metrics, defaultVisibleMetrics }) => {
      const allowedMetrics = new Set(metrics);
      return defaultVisibleMetrics?.every((metric) => allowedMetrics.has(metric)) ?? true;
    },
    {
      path: ['defaultVisibleMetrics'],
      message: 'Visible metrics must belong to the chart metrics.',
    },
  );

export const dashboardPublisherWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.literal(DashboardWidgetType.PublisherPerformance),
  columns: z.array(z.enum(DashboardPublisherColumn)).min(1),
  pageSize: z.number().int().positive().max(100).default(10),
});

export const dashboardDemandIntegrationsWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.literal(DashboardWidgetType.DemandIntegrations),
  pageSize: z.number().int().positive().max(100).default(10),
});

export const dashboardDemandPartnersWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.literal(DashboardWidgetType.DemandPartners),
  pageSize: z.number().int().positive().max(100).default(10),
});

export const dashboardSrpmRankingWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.literal(DashboardWidgetType.SrpmRanking),
  pageSize: z.number().int().positive().max(100).default(10),
});

export const dashboardNetworkWidgetSchema = z.object({
  ...widgetBaseShape,
  type: z.literal(DashboardWidgetType.NetworkPerformance),
  columns: z.array(z.enum(DashboardNetworkColumn)).min(1),
  pageSize: z.number().int().positive().max(100).default(10),
});

export const dashboardWidgetSchema = z.discriminatedUnion('type', [
  dashboardMetricWidgetSchema,
  dashboardPerformanceChartWidgetSchema,
  dashboardPublisherWidgetSchema,
  dashboardDemandIntegrationsWidgetSchema,
  dashboardDemandPartnersWidgetSchema,
  dashboardSrpmRankingWidgetSchema,
  dashboardNetworkWidgetSchema,
]);

export const dashboardDocumentSchema = z.object({
  version: z.literal(1),
  widgets: z.array(dashboardWidgetSchema).superRefine((widgets, context) => {
    const ids = new Set<string>();
    widgets.forEach((widget, index) => {
      if (ids.has(widget.id)) {
        context.addIssue({
          code: 'custom',
          message: 'Dashboard widget IDs must be unique.',
          path: [index, 'id'],
        });
      }
      ids.add(widget.id);
    });
  }),
});

export type DashboardDateScope = z.infer<typeof dashboardDateScopeSchema>;
export type DashboardWidget = z.infer<typeof dashboardWidgetSchema>;
export type DashboardDocument = z.infer<typeof dashboardDocumentSchema>;

export function dashboardMetricValue(
  values: DashboardMetricValues | null | undefined,
  metric: DashboardMetric,
) {
  if (!values) return undefined;
  return values[dashboardMetricValueField[metric]];
}

export function dashboardTimeline(
  overview: Pick<DashboardOverview, 'hours' | 'totalsByDay'>,
  range: DashboardDateRange,
) {
  const preferred = range.dateFrom === range.dateTo ? overview.hours : overview.totalsByDay;
  const fallback = range.dateFrom === range.dateTo ? overview.totalsByDay : overview.hours;
  return preferred.length > 0 ? preferred : fallback;
}
